import { Directive, ElementRef, AfterViewInit, OnDestroy, HostListener, NgZone } from '@angular/core';
import { MutationObserverFactory } from '@angular/cdk/observers';

@Directive({
    selector: 'input[matInput][appEntitySelectorPosition]',
})
export class EntitySelectorPositionDirective implements AfterViewInit, OnDestroy {
    private readonly OPACITY_TRANSITION_DURATION = 0.15; // seconds
    private readonly FOCUS_POSITIONING_DELAY = 10; // ms
    private readonly RESIZE_POSITIONING_DELAY = 50; // ms
    private readonly MIN_SPACE_ABOVE_FOR_TOP_POSITION = 100; // px
    private readonly VIEWPORT_BOTTOM_MARGIN = 10; // px
    private readonly VIEWPORT_RIGHT_MARGIN = 5; // px
    private readonly DROPDOWN_TOP_OFFSET = 20; // px
    private readonly DROPDOWN_Z_INDEX = 1000;

    private observer: MutationObserver;
    private dropdownElement: HTMLElement;
    private isDropdownVisible = false;
    private parentElement: HTMLElement;

    constructor(
        private elementRef: ElementRef,
        private mutationObserverFactory: MutationObserverFactory,
        private ngZone: NgZone,
    ) {}

    ngAfterViewInit() {
        this.parentElement = this.elementRef.nativeElement.closest('app-entity-selector');

        if (!this.parentElement) return;

        // Add a style tag to hide dropdown until positioned
        const styleEl = document.createElement('style');
        styleEl.innerHTML = `
            app-entity-selector .multi-select-group-list:not(.positioned) {
                opacity: 0 !important;
                transition: opacity ${this.OPACITY_TRANSITION_DURATION}s;
            }
            app-entity-selector .multi-select-group-list.positioned {
                opacity: 1 !important;
                transition: opacity ${this.OPACITY_TRANSITION_DURATION}s;
            }
        `;
        document.head.appendChild(styleEl);

        this.observer = this.mutationObserverFactory.create((mutations) => {
            for (const mutation of mutations) {
                if (mutation.type === 'childList') {
                    // Check if dropdown was added or removed
                    const addedDropdown = Array.from(mutation.addedNodes).find((node) =>
                        (node as HTMLElement).classList?.contains('multi-select-group-list'),
                    ) as HTMLElement;

                    if (addedDropdown) {
                        this.isDropdownVisible = true;
                        this.dropdownElement = addedDropdown;
                        this.ngZone.runOutsideAngular(() => {
                            this.positionDropdown();
                            // reposition again to ensure correct placement
                            requestAnimationFrame(() => {
                                this.positionDropdown();
                                this.dropdownElement.classList.add('positioned');
                            });
                        });
                    } else {
                        const dropdownRemoved = Array.from(mutation.removedNodes).some((node) =>
                            (node as HTMLElement).classList?.contains('multi-select-group-list'),
                        );

                        if (dropdownRemoved) {
                            this.isDropdownVisible = false;
                            this.dropdownElement = null;
                        }
                    }
                }
            }
        }) as MutationObserver;

        this.observer.observe(this.parentElement, {
            childList: true,
            subtree: false,
        });
    }

    @HostListener('focus')
    onInputFocus() {
        this.ngZone.runOutsideAngular(() => {
            setTimeout(() => {
                if (!this.dropdownElement) {
                    this.dropdownElement = this.parentElement.querySelector('.multi-select-group-list');
                }

                if (this.dropdownElement) {
                    this.isDropdownVisible = true;
                    this.positionDropdown();
                    requestAnimationFrame(() => {
                        this.dropdownElement.classList.add('positioned');
                    });
                }
            }, this.FOCUS_POSITIONING_DELAY);
        });
    }

    @HostListener('window:scroll', ['$event'])
    onWindowScroll(event: Event) {
        if (this.isDropdownVisible) {
            this.positionDropdown();
        }
    }

    @HostListener('window:resize', ['$event'])
    onResize(event: Event) {
        if (this.isDropdownVisible) {
            this.ngZone.runOutsideAngular(() => {
                setTimeout(() => this.positionDropdown(), this.RESIZE_POSITIONING_DELAY);
            });
        }
    }

    private positionDropdown(): void {
        if (!this.dropdownElement) {
            this.dropdownElement = this.parentElement.querySelector('.multi-select-group-list');
        }

        if (!this.dropdownElement) return;

        const inputRect = this.elementRef.nativeElement.getBoundingClientRect();

        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;

        const dropdownRect = this.dropdownElement.getBoundingClientRect();

        let top = inputRect.bottom;

        // If dropdown would go below viewport, position it above the input
        if (top + dropdownRect.height > viewportHeight) {
            const spaceAbove = inputRect.top;
            const spaceBelow = viewportHeight - inputRect.bottom;

            if (spaceAbove > spaceBelow && spaceAbove >= this.MIN_SPACE_ABOVE_FOR_TOP_POSITION) {
                top = inputRect.top - dropdownRect.height;
            } else {
                this.dropdownElement.style.maxHeight = `${
                    viewportHeight - inputRect.bottom - this.VIEWPORT_BOTTOM_MARGIN
                }px`;
            }
        }

        let left = inputRect.left;

        if (left + dropdownRect.width > viewportWidth) {
            left = viewportWidth - dropdownRect.width - this.VIEWPORT_RIGHT_MARGIN;
        }

        this.dropdownElement.style.position = 'fixed';
        this.dropdownElement.style.top = `${top + this.DROPDOWN_TOP_OFFSET}px`;
        this.dropdownElement.style.left = `${left}px`;
        this.dropdownElement.style.width = `${inputRect.width}px`;
        this.dropdownElement.style.zIndex = `${this.DROPDOWN_Z_INDEX}`;
    }

    ngOnDestroy() {
        if (this.observer) {
            this.observer.disconnect();
        }
    }
}
